import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { ChangeEvent, useEffect, useState } from "react";
import CustomDatePicker, { DateObject } from "react-multi-date-picker";

import { formatAdjustmentType } from "helpers";
import { AdjustmentTypeEnum, BalanceTypeEnum } from "helpers/types";

export interface AdjustmentFormSubmission {
  adjustment_date: string;
  adjustment_type: AdjustmentTypeEnum;
  adjustment_amount: number;
  ignore: boolean;
}

interface CreateAdjustmentModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: AdjustmentFormSubmission) => Promise<unknown> | unknown;
  hasExistingHistory: boolean;
  balanceType: BalanceTypeEnum;
  submitting?: boolean;
  defaultAmount?: number | null;
}

interface FormState {
  adjustment_date: string;
  adjustment_type: AdjustmentTypeEnum;
  adjustment_amount: string;
  ignore: boolean;
}

const getDefaultAdjustmentType = (
  balanceType: BalanceTypeEnum,
  hasExistingHistory: boolean,
): AdjustmentTypeEnum => {
  if (balanceType === BalanceTypeEnum.TRADE_AMOUNT) {
    return AdjustmentTypeEnum.MAX_TRADE_AMOUNT;
  }
  return hasExistingHistory
    ? AdjustmentTypeEnum.BUY
    : AdjustmentTypeEnum.INCEPTION;
};

const buildInitialState = (
  balanceType: BalanceTypeEnum,
  hasExistingHistory: boolean,
): FormState => ({
  adjustment_date: dayjs().format("YYYY-MM-DD"),
  adjustment_type: getDefaultAdjustmentType(balanceType, hasExistingHistory),
  adjustment_amount: "",
  ignore: false,
});

const adjustmentTypeButtonColor = (type: AdjustmentTypeEnum) => {
  if (type === AdjustmentTypeEnum.BUY) return "success";
  if (type === AdjustmentTypeEnum.SELL) return "error";
  if (type === AdjustmentTypeEnum.MAX_TRADE_AMOUNT) return "primary";
  return "warning";
};

export const CreateAdjustmentModal = ({
  open,
  onClose,
  onSubmit,
  hasExistingHistory,
  balanceType,
  submitting,
  defaultAmount,
}: CreateAdjustmentModalProps) => {
  const [form, setForm] = useState<FormState>(() =>
    buildInitialState(balanceType, hasExistingHistory),
  );

  // Reset the form whenever the modal is reopened or context changes
  useEffect(() => {
    if (open) {
      setForm({
        ...buildInitialState(balanceType, hasExistingHistory),
        ...(defaultAmount != null ? { adjustment_amount: String(defaultAmount) } : {}),
      });
    }
  }, [open, hasExistingHistory, balanceType, defaultAmount]);

  const isTradeAmount = balanceType === BalanceTypeEnum.TRADE_AMOUNT;
  const availableAdjustmentTypes = isTradeAmount
    ? [AdjustmentTypeEnum.MAX_TRADE_AMOUNT]
    : hasExistingHistory
      ? [AdjustmentTypeEnum.BUY, AdjustmentTypeEnum.SELL]
      : [AdjustmentTypeEnum.INCEPTION];

  const onAmountChange = (event: ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, adjustment_amount: event.target.value }));

  const handleSubmit = async () => {
    const parsedAmount = Number(form.adjustment_amount);
    if (!form.adjustment_date || Number.isNaN(parsedAmount)) return;
    await onSubmit({
      adjustment_date: form.adjustment_date,
      adjustment_type: form.adjustment_type,
      adjustment_amount: parsedAmount,
      ignore: form.ignore,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Adjust Account Amount</DialogTitle>
      <DialogContent>
        <Stack spacing={2} marginTop={1}>
          <Box>
            <Typography variant="body2" marginBottom={0.5}>
              Date
            </Typography>
            <CustomDatePicker
              format="MM/DD/YYYY"
              portal
              zIndex={1600}
              calendarPosition="bottom-left"
              value={
                form.adjustment_date
                  ? new DateObject({
                      date: form.adjustment_date,
                      format: "YYYY-MM-DD",
                    })
                  : undefined
              }
              onChange={(date: DateObject | DateObject[] | null) => {
                if (!date || Array.isArray(date)) {
                  setForm((prev) => ({ ...prev, adjustment_date: "" }));
                  return;
                }
                setForm((prev) => ({
                  ...prev,
                  adjustment_date: date.format("YYYY-MM-DD"),
                }));
              }}
              style={{ height: "40px", width: "100%", fontSize: "16px" }}
              placeholder="Pick date"
              showOtherDays
            />
          </Box>
          <Box>
            <Typography variant="body2" marginBottom={0.5}>
              Adjustment Type
            </Typography>
            <Stack direction="row" spacing={1}>
              {availableAdjustmentTypes.map((adjustmentType) => (
                <Button
                  key={adjustmentType}
                  type="button"
                  variant={
                    form.adjustment_type === adjustmentType
                      ? "contained"
                      : "outlined"
                  }
                  color={adjustmentTypeButtonColor(adjustmentType)}
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      adjustment_type: adjustmentType,
                    }))
                  }
                >
                  {formatAdjustmentType(adjustmentType)}
                </Button>
              ))}
            </Stack>
          </Box>
          <TextField
            label="Adjustment Amount"
            type="number"
            value={form.adjustment_amount}
            onChange={onAmountChange}
            inputProps={{ min: "0", step: "1" }}
            fullWidth
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.ignore}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, ignore: event.target.checked }))
                }
              />
            }
            label="Ignore (exclude from calculations)"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            submitting || !form.adjustment_date || !form.adjustment_amount
          }
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};
